/**
 * Link utilities for UTM Linker
 * Contains functions for processing and validating links
 */

import {
    isMessagingLink,
    hasUtmParameters,
    hasEmbeddedUtmParameters,
    parseMessagingUrl,
    parseMessagingUrlForRewrite,
    extractUrlParam,
    MESSAGING_PROTOCOLS
  } from './MessagingParser.js';
import { SHORTENER_DOMAINS } from './ShortenerList.js';

// Re-export functions needed by other modules
export { hasUtmParameters, hasEmbeddedUtmParameters, isMessagingLink };

/**
 * Lowercase + strip a leading `www.` + trim. Exported (pr-review-council
 * finding, v1.7.0): LeadBeacon.js's billing-grade EXACT hostname match
 * needs the SAME normalization matchDomainPattern() applies internally,
 * without pulling in matchDomainPattern's own implicit suffix-match
 * semantics (see LeadBeacon.js's `hostMatchesForBilling()`) — reusing this
 * instead of a second hand-rolled normalizer.
 *
 * @param {string} hostname
 * @return {string}
 */
export function normalizeHostname(hostname) {
  return hostname.toLowerCase().replace(/^www\./, '').trim();
}

/**
 * F1 (T3): whether a link's destination is ALREADY a known short-URL —
 * appending utm_* to it is useless-to-harmful (the destination's own UTM,
 * set before shortening, wins after the redirect). Matches the built-in
 * ShortenerList.js snapshot plus any admin-configured additions, using the
 * SAME domain-pattern matcher (`matchDomainPattern`) already used for
 * excludeDomains/click-id-passthrough allowlisting, for consistent
 * case-insensitive + `www.`-stripped + subdomain matching behavior.
 *
 * @param {HTMLAnchorElement|{hostname?: string}} link The link element (or
 *   a hostname-bearing stub, for pure-function tests).
 * @param {string[]} userDomains Admin-configured additional shortener domains.
 * @return {boolean}
 */
export function isShortenerLink(link, userDomains = []) {
  if (!link || !link.hostname) return false;

  const domains = SHORTENER_DOMAINS.concat(Array.isArray(userDomains) ? userDomains : []);
  return domains.some((pattern) => matchDomainPattern(link.hostname, pattern));
}

export function isExternalLink(link, currentHostname) {
  if (!link.hostname) return false;

  const linkHost = normalizeHostname(link.hostname);
  const currentHost = normalizeHostname(currentHostname);

  if (linkHost === currentHost) return false;

  return !linkHost.endsWith('.' + currentHost);
}

export function checkExcludedClasses(link, excludeClasses, debugMode) {
  if (!Array.isArray(excludeClasses) || !link.classList) return false;

  for (const cls of excludeClasses) {
    if (link.classList.contains(cls)) {
      if (debugMode) console.log(`Excluding link with class: ${cls}`);
      return true;
    }
  }

  return false;
}

function checkExcludedProtocols(link, excludeProtocols, debugMode) {
  if (!Array.isArray(excludeProtocols) || !link.href) return false;

  const protocols = [...excludeProtocols, ...MESSAGING_PROTOCOLS];

  for (const protocol of protocols) {
    if (link.href.toLowerCase().startsWith(protocol.toLowerCase())) {
      if (debugMode) console.log(`Excluding link with protocol: ${protocol}`);
      return true;
    }
  }

  return false;
}

function checkExcludedDomains(link, excludeDomains, debugMode) {
  if (!Array.isArray(excludeDomains)) return false;

  const hostname = getRealHostname(link);
  if (!hostname) return false;

  for (const domain of excludeDomains) {
    if (matchDomainPattern(hostname, domain)) {
      if (debugMode) {
        console.log(`Excluding ${hostname} matched pattern: ${domain}`);
      }
      return true;
    }
  }

  return false;
}

/**
 * @param {HTMLAnchorElement} link
 * @param {string[]} excludeClasses
 * @param {string[]} excludeDomains
 * @param {string[]} excludeProtocols
 * @param {boolean} debugMode
 * @param {'skip'|'override'|'merge'} existingUtmMode F2 (T4): whether a link
 *   that already has utm_* params should be excluded outright ('skip', the
 *   pre-1.6.0 default and only behavior), or left to appendUtmParams()'s own
 *   override/merge handling below in processLinks() ('override'/'merge').
 *   Defaults to 'skip' so every pre-1.6.0 caller (positional, no 6th
 *   argument) is byte-identical.
 * @return {boolean}
 */
export function shouldExcludeLink(link, excludeClasses, excludeDomains, excludeProtocols = [], debugMode = false, existingUtmMode = 'skip') {
  if (!link) return true;

  if (link.hasAttribute?.('data-no-utm')) {
    if (debugMode) console.log('Excluding link with data-no-utm attribute');
    return true;
  }

  if (checkExcludedClasses(link, excludeClasses, debugMode)) {
    return true;
  }

  if (link.href && link.href.toLowerCase().includes('whatsapp://send')) {
    if (debugMode) console.log('Explicitly excluding whatsapp://send link');
    return true;
  }

  if (link.href && isMessagingLink(link)) {
    if (debugMode) console.log(`Excluding messaging link: ${link.href}`);
    return true;
  }

  if (existingUtmMode === 'skip' && link.href && hasUtmParameters(link.href)) {
    if (debugMode) console.log('Excluding link that already has UTM parameters');
    return true;
  }

  if (link.href && link.href.toLowerCase().startsWith('whatsapp:')) {
    if (debugMode) console.log('Checking WhatsApp link for exclusion:', link.href);

    if (hasEmbeddedUtmParameters(link.href)) {
      if (debugMode) console.log('Excluding WhatsApp link with embedded UTM parameters');
      return true;
    }
  }

  if (link.href && hasEmbeddedUtmParameters(link.href)) {
    if (debugMode) console.log('Excluding messaging link with embedded UTM parameters');
    return true;
  }

  if (checkExcludedProtocols(link, excludeProtocols, debugMode)) {
    return true;
  }

  if (checkExcludedDomains(link, excludeDomains, debugMode)) {
    return true;
  }

  return false;
}

function getRealHostname(link) {
  try {
    if (!link || !link.href) return null;

    if (/^(https?:|ftp:)/.test(link.href)) {
      const url = new URL(link.href);
      return url.hostname;
    }

    if (isMessagingLink(link)) {
      const parsedUrl = parseMessagingUrl(link.href);
      if (parsedUrl?.embeddedUrl) {
        try {
          const embeddedUrl = new URL(parsedUrl.embeddedUrl);
          return embeddedUrl.hostname;
        } catch (e) {
          console.warn('UTM Linker: getRealHostname embedded URL parse failed:', e.message);
        }
      }
      return null;
    }

    const embeddedUrl = extractUrlParam(link.href);
    if (embeddedUrl) {
      try {
        return new URL(embeddedUrl).hostname;
      } catch (e) {
        console.warn('UTM Linker: getRealHostname extracted URL parse failed:', e.message);
      }
    }
  } catch (e) {
    console.warn('UTM Linker: getRealHostname parse failed:', e.message);
  }

  return null;
}

export function matchDomainPattern(hostname, pattern) {
  if (!hostname || !pattern) return false;

  const linkHost = normalizeHostname(hostname);
  const patternNorm = normalizeHostname(pattern);

  if (linkHost === patternNorm) return true;

  if (patternNorm.startsWith('*.')) {
    const base = patternNorm.substring(2);
    return linkHost === base || linkHost.endsWith(`.${base}`);
  }

  return linkHost === patternNorm || linkHost.endsWith(`.${patternNorm}`);
}

/**
 * @param {string} url
 * @param {Object} params
 * @param {Object} i18n
 * @param {'skip'|'override'|'merge'} existingUtmMode F2 (T4): 'skip' (the
 *   pre-1.6.0 default and only behavior) leaves a URL that already has ANY
 *   utm_* param completely untouched; 'override' overwrites every param key
 *   we generate, whether or not it was already present; 'merge' only ADDS a
 *   key that was not already present, preserving any pre-existing value.
 *   Defaults to 'skip' so every pre-1.6.0 caller (positional, no 4th
 *   argument) is byte-identical.
 * @return {string}
 */
export function appendUtmParams(url, params, i18n = {}, existingUtmMode = 'skip') {
  if (typeof url !== 'string') return url;

  if (url.toLowerCase().includes('whatsapp://send')) {
    if (i18n?.debug) console.log('Skipping UTM append for whatsapp://send:', url);
    return url;
  }

  if (isMessagingLink(url) || hasEmbeddedUtmParameters(url)) {
    if (i18n?.debug) console.log('Skipping UTM append for messaging link:', url);
    return url;
  }

  if (existingUtmMode === 'skip' && hasUtmParameters(url)) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const hash = urlObj.hash;

    Object.entries(params).forEach(([key, value]) => {
      if (!key || !value) return;

      // Match existing params CASE-INSENSITIVELY (pr-review-council finding,
      // v1.6.0) — a URL can already carry e.g. `?UTM_Source=old` (case
      // varies in the wild; hasUtmParameters() is itself already
      // case-insensitive, see MessagingParser.js). Exact-case
      // URLSearchParams.has()/.set() would otherwise leave a stale,
      // differently-cased key sitting ALONGSIDE our canonical lowercase
      // one — neither properly preserved (merge) nor properly replaced
      // (override), just duplicated.
      const lowerKey = key.toLowerCase();
      const matchingKeys = Array.from(urlObj.searchParams.keys()).filter(
        (existingKey) => existingKey.toLowerCase() === lowerKey
      );

      if (existingUtmMode === 'merge' && matchingKeys.length > 0) return;

      // override (or a genuinely new key under either mode): drop every
      // differently-cased occurrence first, so only the canonical
      // lowercase key survives the `.set()` below.
      matchingKeys.forEach((existingKey) => {
        if (existingKey !== key) urlObj.searchParams.delete(existingKey);
      });

      urlObj.searchParams.set(key, value);
    });

    let resultUrl = urlObj.toString();
    if (hash && !resultUrl.includes(hash)) {
      resultUrl = resultUrl.replace(/#.*$/, '') + hash;
    }

    return resultUrl;
  } catch (error) {
    console.warn(i18n?.error?.invalidUrl || 'UTM Linker: Invalid URL format', url, error);
    return appendUtmParamsFallback(url, params, existingUtmMode);
  }
}

/**
 * Rewrite a messenger deep-link's EMBEDDED destination URL to carry UTM params,
 * then rebuild the messenger link around the tagged destination. Companion to
 * appendUtmParams() — used by the opt-in messenger-rewrite feature instead of
 * excluding the link outright.
 *
 * Reconstruction is per messenger type:
 *  - whatsapp / viber: the destination lives inside a decoded `text=` message;
 *    only the destination substring within that message is tagged, the message
 *    is re-encoded, and only the `text=` param's value is replaced (the rest of
 *    the href is untouched).
 *  - telegram / vk / generic: the destination lives directly in a
 *    `url=`/`link=`/`u=` param; only that param's value is replaced.
 *
 * Uses parseMessagingUrlForRewrite() (not parseMessagingUrl()) so Telegram/VK
 * share-widget links resolve their real `url=` destination — that correction
 * is intentionally scoped to this rewrite-only path; see that function's doc
 * comment for why it must not change parseMessagingUrl()'s own output.
 *
 * @param {string} href The original messenger link
 * @param {Object} params UTM params to append to the embedded destination
 * @return {string} The rebuilt href, or the original href if nothing could be rewritten
 */
export function rewriteMessagingUrl(href, params) {
  if (typeof href !== 'string' || !href) return href;

  const parsed = parseMessagingUrlForRewrite(href);
  if (!parsed?.embeddedUrl) return href;

  const taggedUrl = appendUtmParams(parsed.embeddedUrl, params);
  if (taggedUrl === parsed.embeddedUrl) return href;

  if (parsed.messageText) {
    const escaped = parsed.embeddedUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const newText = parsed.messageText.replace(new RegExp(escaped, 'g'), taggedUrl);
    return replaceEncodedParam(href, /([?&]text=)([^&]+)/gi, parsed.messageText, encodeURIComponent(newText));
  }

  return replaceEncodedParam(
    href,
    /([?&](?:url|link|u)=)([^&]+)/gi,
    parsed.embeddedUrl,
    encodeURIComponent(taggedUrl)
  );
}

/**
 * Replace the value of a specific query-string param occurrence — matched by
 * its DECODED value, not by param name alone — leaving the rest of the URL
 * untouched. Fails closed (returns the original href) if no occurrence's
 * decoded value matches.
 *
 * @param {string} href The URL to rewrite
 * @param {RegExp} regex Global regex with capture groups [prefix, encodedValue]
 * @param {string} oldDecodedValue Decoded value to match against
 * @param {string} newEncodedValue Pre-encoded replacement value
 * @return {string} The rewritten URL, or the original href if no match was found
 */
function replaceEncodedParam(href, regex, oldDecodedValue, newEncodedValue) {
  let replaced = false;

  const result = href.replace(regex, (fullMatch, prefix, encodedValue) => {
    if (replaced) return fullMatch;

    let decoded;
    try {
      decoded = decodeURIComponent(encodedValue);
    } catch (e) {
      decoded = encodedValue;
    }

    if (decoded === oldDecodedValue) {
      replaced = true;
      return prefix + newEncodedValue;
    }

    return fullMatch;
  });

  return replaced ? result : href;
}

/**
 * @param {string} url
 * @param {Object} params
 * @param {'skip'|'override'|'merge'} existingUtmMode F2 (T4): only 'skip'
 *   (the pre-1.6.0 default and only behavior) short-circuits here — this
 *   fallback only runs when `new URL()` threw in appendUtmParams(), so there
 *   is no parsed URL object to do a real per-key merge against. 'merge' is
 *   documented to behave like 'override' on this rare malformed-URL edge
 *   (both just append every param), rather than adding real per-key merge
 *   complexity for a path nothing in practice exercises.
 * @return {string}
 */
export function appendUtmParamsFallback(url, params, existingUtmMode = 'skip') {
  if (typeof url !== 'string') return url;

  if (url.toLowerCase().includes('whatsapp://send')) {
    return url;
  }

  if (isMessagingLink(url) || hasEmbeddedUtmParameters(url)) {
    return url;
  }

  if (existingUtmMode === 'skip' && hasUtmParameters(url)) {
    return url;
  }

  let hash = '';
  let baseUrl = url;
  const hashIndex = url.indexOf('#');

  if (hashIndex !== -1) {
    hash = url.substring(hashIndex);
    baseUrl = url.substring(0, hashIndex);
  }

  const hasParams = baseUrl.indexOf('?') !== -1;

  const filteredParams = Object.entries(params)
    .filter(([key, value]) => key && value)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  if (filteredParams) {
    return baseUrl + (hasParams ? '&' : '?') + filteredParams + hash;
  }

  return baseUrl + hash;
}
